import React, { useEffect, useRef, useState } from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney, splitPriceCents } from '../../util/currency';
import { ensureListing, ensureUser, profileSlug, userDisplayNameAsString } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { getSellerVacation } from '../../util/vacation';
import { conditionLabel } from '../../util/listingCondition';

import {
  AspectRatioWrapper,
  AvatarSmall,
  Icons,
  NamedLink,
  ResponsiveImage,
} from '../../components';

import css from './ListingCard.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { isArrayLength, showToaster } from '../../util/genericHelpers';
import { selectWishlistIds, toggleWishlistItem } from '../../ducks/user.duck';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

// "$1,234.56" renders with superscript, digit-top-aligned cents. Prices that
// don't end in a cents group (unsupported currency message, unusual locales)
// fall back to the plain string.
const PriceWithSuperscriptCents = ({ formattedPrice }) => {
  const { whole, cents } = splitPriceCents(formattedPrice);
  if (!cents) return <>{formattedPrice}</>;
  return (
    <>
      {whole}
      <span className={css.priceCents}>{cents}</span>
    </>
  );
};

const PriceMaybe = props => {
  const { price, publicData, config, intl, isSold } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={classNames(css.price, { [css.priceSold]: isSold })} title={priceTitle}>
      <PriceWithSuperscriptCents formattedPrice={formattedPrice} />
      {isBookable ? (
        <span className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </span>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    isSearchPage,
  } = props;

  const dispatch = useDispatch();

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const currentStock = currentListing.currentStock?.attributes?.quantity;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const isSold = !isBookable && typeof currentStock === 'number' && currentStock === 0;
  const conditionKey = publicData?.condition;
  const conditionName = !isSold ? conditionLabel(intl, conditionKey) : null;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorUsername = userDisplayNameAsString(author, '');
  // Kept in search, flagged: the seller is on vacation (util/vacation.js).
  const isSellerAway = !!getSellerVacation(author);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const secondImage =
    currentListing.images && currentListing.images.length > 1 ? currentListing.images[1] : null;

  // Phone rows: swipeable photo track + dots (prototype); desktop keeps the
  // static first photo + hover second. The carousel only MOUNTS on phone —
  // rendering it everywhere fetched up to 5 images per card on widths where
  // CSS hides it (Aleksa's review: ~180 wasted requests on a desktop page).
  const [isPhoneViewport, setIsPhoneViewport] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 549px)');
    const update = () => setIsPhoneViewport(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const carouselImages = (currentListing.images || []).slice(0, 5);
  const hasCarousel = isPhoneViewport && carouselImages.length > 1;
  const [activeDot, setActiveDot] = useState(0);
  const trackRafRef = useRef(null);
  useEffect(
    () => () => {
      if (trackRafRef.current) cancelAnimationFrame(trackRafRef.current);
    },
    []
  );
  const onTrackScroll = e => {
    const el = e.currentTarget;
    if (trackRafRef.current) return;
    trackRafRef.current = requestAnimationFrame(() => {
      trackRafRef.current = null;
      setActiveDot(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
    });
  };

  // Mouse drag-to-scroll (Reverb pattern): desktop-pointer environments —
  // including phone-skin simulators that keep the mouse a mouse — never pan
  // an overflow container natively. Real touch keeps native panning
  // (pointerType 'touch' is ignored here). A real drag suppresses the card
  // link's click; snap is lifted during the drag and re-settled on release.
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const onTrackPointerDown = e => {
    if (e.pointerType !== 'mouse') return;
    const el = e.currentTarget;
    dragRef.current = { startX: e.clientX, startLeft: el.scrollLeft, dragged: false };
    el.setPointerCapture(e.pointerId);
  };
  const onTrackPointerMove = e => {
    const s = dragRef.current;
    if (!s) return;
    const el = e.currentTarget;
    const dx = e.clientX - s.startX;
    if (!s.dragged && Math.abs(dx) > 4) {
      s.dragged = true;
      el.style.scrollSnapType = 'none';
    }
    if (s.dragged) {
      el.scrollLeft = s.startLeft - dx;
      e.preventDefault();
    }
  };
  const onTrackPointerUp = e => {
    const s = dragRef.current;
    dragRef.current = null;
    if (!s) return;
    const el = e.currentTarget;
    if (s.dragged) {
      suppressClickRef.current = true;
      const slide = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
      el.style.scrollSnapType = '';
      el.scrollTo({ left: slide * el.clientWidth, behavior: 'smooth' });
    }
  };
  const onTrackClick = e => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variantsFor = image => {
    const allVariants = image?.attributes?.variants ? Object.keys(image.attributes.variants) : [];
    const prefixVariants = allVariants.filter(k => k.startsWith(variantPrefix));
    return prefixVariants.length > 0
      ? prefixVariants
      : allVariants.filter(k => k.startsWith('scaled-')).length > 0
      ? allVariants.filter(k => k.startsWith('scaled-'))
      : allVariants;
  };
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const wishlist = useSelector(selectWishlistIds);
  const isFavorited = isArrayLength(wishlist) && wishlist.findIndex(e => e === id) > -1;

  return (
    <div className={classNames(css.root, className)}>
      {/* Wishlist heart — its own click target above the card link */}
      <button
        type="button"
        className={classNames(css.favoriteIcon, { [css.favorited]: isFavorited })}
        aria-pressed={isFavorited}
        aria-label={intl.formatMessage({ id: 'ListingCard.favorite' }, { title })}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          if (!id) {
            return;
          }
          // The heart changes at once and the save shows the busy cursor; a failed
          // save puts the heart back and says so.
          dispatch(toggleWishlistItem(id)).then(saved => {
            if (!saved) {
              showToaster('error', intl.formatMessage({ id: 'ListingCard.favoriteFailed' }));
            }
          });
        }}
      >
        <Icons name={isFavorited ? 'favorite' : 'unfavorite'} />
      </button>

      {/* Main listing link wrapping image and info */}
      <NamedLink
        className={classNames(rootClassName || css.listingLink)}
        name="ListingPage"
        params={{ id, slug }}
      >
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          {firstImage ? (
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={firstImage}
              variants={variantsFor(firstImage)}
              sizes={renderSizes}
            />
          ) : (
            // No photo yet: a quiet tile, not a message
            <span className={css.noPhoto} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <circle cx="9" cy="10" r="1.6" />
                <path d="m3 17 5-5 4 4 3-3 6 6" />
              </svg>
            </span>
          )}
          {/* Shown on hover only, so it loads only as the card nears the screen, and never
              on touch screens (CSS hides it there) or where the phone carousel is */}
          {secondImage && !hasCarousel ? (
            <div className={css.secondImageWrapper}>
              <ResponsiveImage
                rootClassName={css.rootForImage}
                alt=""
                image={secondImage}
                variants={variantsFor(secondImage)}
                sizes={renderSizes}
                loading="lazy"
              />
            </div>
          ) : null}
          {hasCarousel ? (
            <>
              {/* Photos crossfade (Reverb-style dissolve) while the
                  transparent track below handles swipe + snap */}
              <div className={css.photoStack}>
                {carouselImages.map((image, index) => (
                  <div
                    className={classNames(css.fadeSlide, {
                      [css.fadeSlideActive]: index === activeDot,
                    })}
                    key={image.id?.uuid || index}
                  >
                    <ResponsiveImage
                      rootClassName={css.rootForImage}
                      alt={title}
                      image={image}
                      variants={variantsFor(image)}
                      sizes={renderSizes}
                    />
                  </div>
                ))}
              </div>
              <div
                className={css.photoTrack}
                onScroll={onTrackScroll}
                onPointerDown={onTrackPointerDown}
                onPointerMove={onTrackPointerMove}
                onPointerUp={onTrackPointerUp}
                onPointerCancel={onTrackPointerUp}
                onClick={onTrackClick}
              >
                {carouselImages.map((image, index) => (
                  <div className={css.photoSlide} key={image.id?.uuid || index} />
                ))}
              </div>
              <div className={css.photoDots}>
                {carouselImages.map((_, index) => (
                  <span
                    key={index}
                    className={classNames(css.photoDot, {
                      [css.photoDotActive]: index === activeDot,
                    })}
                  />
                ))}
              </div>
            </>
          ) : null}
          {/* The image carries ONLY the heart; sold listings dim the photo
              (ledger §3, corrected 2026-08-25) */}
          {isSold ? <div className={css.soldOverlay} /> : null}
        </AspectRatioWrapper>
        <div className={css.info}>
          <h2 className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </h2>
        </div>
      </NamedLink>

      {/* Card body bottom (ledger §3, FINAL 2026-08-25): seller row on its own
          full-width line, then the bottom line = condition badge left / price
          right. No category line on any card. */}
      {showAuthorInfo && profileSlug(author) ? (
        <NamedLink
          className={css.sellerRow}
          name="ProfilePage"
          params={{ username: profileSlug(author) }}
        >
          {/* A seller without a photo gets the profile page's person picture */}
          <AvatarSmall
            user={author}
            className={css.sellerAvatar}
            placeholder="person"
            disableProfileLink
          />
          <span className={css.sellerName}>{authorUsername}</span>
        </NamedLink>
      ) : (
        <span className={css.sellerRowSpacer} />
      )}
      <div className={css.bottomLine}>
        {isSold ? (
          <span className={classNames(css.conditionBadge, css.soldBadge)}>
            <FormattedMessage id="ListingCard.outOfStock" />
          </span>
        ) : conditionName ? (
          <span
            className={classNames(css.conditionBadge, {
              [css.conditionBadgeNew]: conditionKey === 'new',
            })}
          >
            {conditionName}
          </span>
        ) : null}
        {isSellerAway ? (
          <span className={classNames(css.conditionBadge, css.awayBadge)}>
            <FormattedMessage id="ListingCard.awayBadge" />
          </span>
        ) : null}
        <span className={css.bottomSpacer} />
        <PriceMaybe
          price={price}
          publicData={publicData}
          config={config}
          intl={intl}
          isSold={isSold}
        />
      </div>
    </div>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  isSearchPage: false,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,
  isSearchPage: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
